import React, { useRef, useState } from 'react';
import { Button, ErrorState } from '../components/ui';
import { useSession } from '../context/SessionContext';
import { useTheme } from '../theme/ThemeProvider';
import { supabase } from '../lib/supabaseClient';

const OTP_LENGTH = 6;

export function SignInScreen() {
  const { signIn, signUp, signInWithProvider } = useSession();
  const { language, setLanguage } = useTheme();
  const [mode, setMode] = useState<'signin' | 'signup'>(() => typeof window !== 'undefined' && window.location.hash === '#create-account' ? 'signup' : 'signin');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [providerSubmitting, setProviderSubmitting] = useState<'google' | 'apple' | null>(null);
  // AuthVerify.dc.html's 6-digit code, backed for real by Supabase's own
  // OTP verification (supabase.auth.verifyOtp({ email, token, type: 'email' }))
  // -- the same code Supabase emails whether the account was just created
  // (signup confirmation) or already exists (magic-link sign-in), so this
  // one screen covers both without a separate OZIRA-side code system.
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  function changeMode(nextMode: 'signin' | 'signup') {
    setMode(nextMode);
    setError(null);
    setConfirmationEmail(null);
    setResendStatus('idle');
    setOtp(Array(OTP_LENGTH).fill(''));
    setOtpError(null);
    window.history.replaceState(null, '', nextMode === 'signup' ? '#create-account' : window.location.pathname);
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (mode === 'signup') {
        if (!name.trim()) throw new Error('Please enter your name.');
        if (password.length < 8) throw new Error('Password must be at least 8 characters.');
        if (password !== confirmPassword) throw new Error('Passwords do not match.');
        const result = await signUp(name, email, password);
        if (result.needsEmailConfirmation) {
          setConfirmationEmail(email);
          setResendStatus('idle');
        }
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in. Check your details and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!confirmationEmail) return;
    setResendStatus('sending');
    try {
      const { error: resendError } = await supabase.auth.resend({ type: 'signup', email: confirmationEmail });
      setResendStatus(resendError ? 'error' : 'sent');
    } catch (_) {
      setResendStatus('error');
    }
  }

  function setOtpDigit(index: number, rawValue: string) {
    const digits = rawValue.replace(/\D/g, '');
    if (!digits) {
      setOtp((prev) => { const next = [...prev]; next[index] = ''; return next; });
      return;
    }
    setOtp((prev) => {
      const next = [...prev];
      for (let i = 0; i < digits.length && index + i < OTP_LENGTH; i += 1) next[index + i] = digits[i];
      return next;
    });
    const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
    otpInputRefs.current[nextIndex]?.focus();
  }

  function handleOtpKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  }

  async function handleConfirmOtp(event: React.FormEvent) {
    event.preventDefault();
    if (!confirmationEmail) return;
    const token = otp.join('');
    if (token.length !== OTP_LENGTH) {
      setOtpError(`Enter the full ${OTP_LENGTH}-digit code.`);
      return;
    }
    setOtpSubmitting(true);
    setOtpError(null);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({ email: confirmationEmail, token, type: 'email' });
      if (verifyError) throw new Error(verifyError.message);
      // A successful verifyOtp sets a real Supabase session; SessionContext's
      // own onAuthStateChange listener picks it up and the app re-renders
      // into the authenticated Assistant workspace on its own.
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : 'That code is invalid or has expired.');
    } finally {
      setOtpSubmitting(false);
    }
  }

  async function handleProvider(provider: 'google' | 'apple') {
    setProviderSubmitting(provider);
    setError(null);
    setConfirmationEmail(null);
    try {
      await signInWithProvider(provider);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not continue with ${provider}.`);
    } finally {
      setProviderSubmitting(null);
    }
  }

  return (
    <div className="ozira-sign-in-layout">
      <aside className="ozira-sign-in-brand" aria-label="OZIRA brand">
        <div className="ozira-sign-in-brand__lockup">
          <div className="ozira-sign-in-brand__mark" aria-hidden="true">
            <img src="/logo.png" alt="" />
          </div>
          <div>
            <div className="ozira-sign-in-brand__name">OZIRA</div>
            <div className="ozira-sign-in-brand__tagline">TRUST BY DESIGN</div>
          </div>
        </div>
        <div className="ozira-sign-in-brand__message">
          <h2>One trusted place<br />for real work.</h2>
          <p>Ask. Build. Organize. Decide with confidence —<br />across your personal and organization context.</p>
        </div>
        <div className="ozira-sign-in-brand__footer">
          <div className="ozira-sign-in-brand__platforms">Web · iOS · Android · Desktop</div>
          <div className="ozira-sign-in-brand__links" aria-label="OZIRA links">
            <a href="https://www.linkedin.com/company/oziraofficial/" target="_blank" rel="noreferrer" aria-label="OZIRA on LinkedIn">in</a>
            <a href="https://x.com/oziraofficial?s=11" target="_blank" rel="noreferrer" aria-label="OZIRA on X">X</a>
          </div>
        </div>
      </aside>

      {confirmationEmail ? (
        <form className="ozira-sign-in-form ozira-verify-email" onSubmit={handleConfirmOtp}>
          <div className="ozira-verify-email__icon" aria-hidden="true">✉</div>
          <h1>Verify your email</h1>
          <p className="ozira-verify-email__body">
            We sent a 6-digit code to <strong>{confirmationEmail}</strong>. It expires in 24 hours.
          </p>
          <label className="ozira-otp" aria-label="Verification code">
            <span className="ozira-otp__label">Enter code</span>
            <span className="ozira-otp__boxes">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { otpInputRefs.current[index] = el; }}
                  className="ozira-otp__box"
                  inputMode="numeric"
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  maxLength={OTP_LENGTH}
                  value={digit}
                  onChange={(e) => setOtpDigit(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  autoFocus={index === 0}
                />
              ))}
            </span>
          </label>
          {otpError ? <ErrorState title="Could not verify" description={otpError} /> : null}
          {resendStatus === 'sent' ? <p className="ozira-sign-in-success" role="status">Code resent.</p> : null}
          {resendStatus === 'error' ? <ErrorState title="Could not resend" description="Please try again in a moment." /> : null}
          <Button type="submit" disabled={otpSubmitting}>
            {otpSubmitting ? 'Confirming…' : mode === 'signup' ? 'Confirm and create account' : 'Confirm'}
          </Button>
          <p className="ozira-verify-email__resend">
            Didn’t get it?{' '}
            <a href="#resend" onClick={(event) => { event.preventDefault(); void handleResend(); }}>
              {resendStatus === 'sending' ? 'Resending…' : 'Resend code'}
            </a>
          </p>
          <p className="ozira-sign-in-create">
            <a href="/" onClick={(event) => { event.preventDefault(); changeMode('signin'); }}>Back to sign in</a>
          </p>
        </form>
      ) : (
        <form className="ozira-sign-in-form" onSubmit={handleSubmit}>
          <label className="ozira-sign-in-language">
            <span className="sr-only">Language</span>
            <select value={language} onChange={(event) => setLanguage(event.target.value)}>
              <option value="en">EN</option>
              <option value="am">አማ</option>
              <option value="om">OM</option>
              <option value="ar">AR</option>
            </select>
          </label>
          <div className="ozira-sign-in-form__intro">
            <h1>{mode === 'signup' ? 'Create your account' : 'Welcome back'}</h1>
            <p>{mode === 'signup' ? 'Join OZIRA and bring your work together.' : 'Sign in to continue to OZIRA'}</p>
          </div>
          {mode === 'signup' ? <label className="ozira-sign-in-field"><span>Name</span><input type="text" required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Your name" /></label> : null}
          <label className="ozira-sign-in-field">
            <span>Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@company.com" />
          </label>
          <label className="ozira-sign-in-field">
            <span>Password</span>
            <span className="ozira-sign-in-password">
              <input type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} placeholder="••••••••" />
              <button type="button" onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? 'Hide' : 'Show'}</button>
            </span>
          </label>
          {mode === 'signup' ? <label className="ozira-sign-in-field"><span>Confirm password</span><input type={showPassword ? 'text' : 'password'} required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" placeholder="••••••••" /></label> : null}
          {error ? <ErrorState title="Sign in failed" description={error} /> : null}
          <Button type="submit" disabled={submitting}>
            {submitting ? (mode === 'signup' ? 'Creating account…' : 'Signing in…') : (mode === 'signup' ? 'Create account' : 'Sign in')}
          </Button>
          <div className="ozira-sign-in-divider"><span>or continue with</span></div>
          <div className="ozira-sign-in-providers">
            <button type="button" onClick={() => void handleProvider('google')} disabled={providerSubmitting !== null || submitting} aria-label="Continue with Google">
              <span className="ozira-provider-mark ozira-provider-mark--google" aria-hidden="true">G</span>
              <span>{providerSubmitting === 'google' ? 'Connecting…' : 'Google'}</span>
            </button>
            <button type="button" onClick={() => void handleProvider('apple')} disabled={providerSubmitting !== null || submitting} aria-label="Continue with Apple">
              <span className="ozira-provider-mark ozira-provider-mark--apple" aria-hidden="true">●</span>
              <span>{providerSubmitting === 'apple' ? 'Connecting…' : 'Apple'}</span>
            </button>
          </div>
          <p className="ozira-sign-in-terms">By continuing you agree to the Terms and Privacy Policy.</p>
          <p className="ozira-sign-in-create">{mode === 'signup' ? <>Already have an account? <a href="/" onClick={(event) => { event.preventDefault(); changeMode('signin'); }}>Sign in</a></> : <>Don’t have an account? <a href="#create-account" onClick={(event) => { event.preventDefault(); changeMode('signup'); }}>Create account</a></>}</p>
        </form>
      )}
    </div>
  );
}
